import React from "react";
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import MaterialButton from '@mui/material/Button';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import DatePicker from "react-datepicker";
import { nb } from 'date-fns/locale';
import "react-datepicker/dist/react-datepicker.css";
import "./date-picker.css";

import GroupSelect from "../GroupSelect";
import SeasonSwitch from "../SeasonSwitch";
import winterBackground from '../../assets/Bakgrunn_aktive-deg_winter.png';
import summerBackground from '../../assets/Bakgrunn_aktive-deg_summer.png';

const norwegianDatePickerLocale = {
    ...nb,
    localize: {
        ...nb.localize,
        month: (month, options) => {
            const monthName = nb.localize.month(month, options);
            return monthName.charAt(0).toUpperCase() + monthName.slice(1);
        },
    },
};

export default function NewUserGroupDialog({
    open,
    onClose,
    onSave,
    userInst,
    onPasteTextChange,
    parsedUsers,
    onUsersChange,
    group,
    onGroupChange,
    arrivalDate,
    onArrivalDateChange,
    departureDate,
    onDepartureDateChange,
    isWinter,
    onIsWinterChange,
}) {
    const validUserCount = parsedUsers.filter((user) => user.firstName.trim() && user.userId.trim()).length;
    const isGroupMissing = userInst === '01' && !group.trim();
    const seasonAccent = isWinter ? '#439AC2' : '#7FAF84';
    const seasonAccentHover = isWinter ? '#2F86AD' : '#6E9672';
    const seasonOutline = isWinter ? '#9FC8DA' : '#B6D0B8';
    const seasonBackground = isWinter ? winterBackground : summerBackground;

    const handlePaste = (event) => {
        event.preventDefault();
        onPasteTextChange(event.clipboardData.getData('text'));
    };

    // Merges an edited column's lines with the other column's existing values, keyed by row index.
    const mergeColumn = (key, text) => {
        const lines = text.split('\n');
        const otherKey = key === 'firstName' ? 'userId' : 'firstName';
        const users = lines.map((line, index) => ({
            [key]: line,
            [otherKey]: parsedUsers[index]?.[otherKey] ?? '',
        }));
        onUsersChange(users);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '10px',
                    position: 'relative',
                    overflow: 'hidden',
                    maxWidth: 770,
                    boxShadow: '0 20px 50px rgba(15, 23, 42, 0.2)',
                    backgroundColor: '#FFFFFF',
                    backgroundImage: `url(${seasonBackground})`,
                    backgroundPosition: 'center',
                    backgroundSize: 'cover',
                    backgroundRepeat: 'no-repeat',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        backgroundColor: 'rgba(253, 254, 255, 0.25)',
                        pointerEvents: 'none',
                    },
                    '& > *': {
                        position: 'relative',
                        zIndex: 1,
                    },
                    '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255, 255, 255, 0.68)',
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                        borderRadius: '14px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: seasonOutline,
                        borderWidth: '3px',
                        borderRadius: '14px',
                    },
                    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: seasonOutline,
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: seasonOutline,
                    },
                },
            }}
        >
            <Box sx={{ mx: 6, my: 5, overflow: 'hidden', border: '1px solid rgba(255, 255, 255, 0.72)', borderRadius: '14px', backgroundColor: 'rgba(248, 252, 254, 0.9)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                <DialogTitle sx={{ px: 5, pt: 4, pb: 1, color: '#17233D', fontSize: 28, fontWeight: 700 }}>
                    Opprett brukergruppe
                </DialogTitle>
                <DialogContent sx={{ px: 5, py: 1.5 }}>
                    <DialogContentText sx={{ mb: 2.5, color: '#687895', fontSize: 18 }}>
                        Lim inn fornavn og pasientnummer fra tabell (vekslende linjer, fornavn først). Alle felt er obligatoriske.
                    </DialogContentText>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 2, mb: 2.5 }}>
                        <TextField
                            autoFocus
                            id="pasteFirstNames"
                            label="Fornavn"
                            fullWidth
                            multiline
                            minRows={4}
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            value={parsedUsers.map((user) => user.firstName).join('\n')}
                            onPaste={handlePaste}
                            onChange={(event) => mergeColumn('firstName', event.target.value)}
                            required
                        />
                        <TextField
                            id="pastePatientNumbers"
                            label="Pasientnummer"
                            fullWidth
                            multiline
                            minRows={4}
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            value={parsedUsers.map((user) => user.userId).join('\n')}
                            onPaste={handlePaste}
                            onChange={(event) => mergeColumn('userId', event.target.value)}
                            required
                        />
                    </Box>
                    {validUserCount > 0 && (
                        <Typography variant="body2">{validUserCount} nye brukere</Typography>
                    )}
                    <Box sx={{ mt: 1.5 }}>
                        <GroupSelect userInst={userInst} group={group} onChange={onGroupChange} required isWinter={isWinter} />
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, mt: 0.5 }}>
                        <Box>
                            <Typography sx={{ mb: 0.5, color: '#17233D', fontSize: 15, fontWeight: 500 }}>Ankomstdato <Box component="span" sx={{ color: seasonAccent }}>*</Box></Typography>
                            <DatePicker
                                selected={arrivalDate}
                                onChange={onArrivalDateChange}
                                dateFormat="dd.MM.yyyy"
                                locale={norwegianDatePickerLocale}
                                customInput={(
                                    <TextField
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        sx={{ '& .MuiOutlinedInput-root': { height: 48, minHeight: 48 } }}
                                        InputProps={{
                                            endAdornment: <InputAdornment position="end"><CalendarTodayOutlinedIcon sx={{ color: '#425575', fontSize: 20 }} /></InputAdornment>,
                                        }}
                                    />
                                )}
                            />
                        </Box>
                        <Box>
                            <Typography sx={{ mb: 0.5, color: '#17233D', fontSize: 15, fontWeight: 500 }}>Avreisedato <Box component="span" sx={{ color: seasonAccent }}>*</Box></Typography>
                            <DatePicker
                                selected={departureDate}
                                minDate={arrivalDate}
                                onChange={onDepartureDateChange}
                                dateFormat="dd.MM.yyyy"
                                locale={norwegianDatePickerLocale}
                                customInput={(
                                    <TextField
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        sx={{ '& .MuiOutlinedInput-root': { height: 48, minHeight: 48 } }}
                                        InputProps={{
                                            endAdornment: <InputAdornment position="end"><CalendarTodayOutlinedIcon sx={{ color: '#425575', fontSize: 20 }} /></InputAdornment>,
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    </Box>
                    <SeasonSwitch userInst={userInst} isWinter={isWinter} onChange={onIsWinterChange} accent={seasonAccent} accentHover={seasonAccentHover} outline={seasonOutline} />
                </DialogContent>
                <DialogActions sx={{ px: 5, py: 2.5, borderTop: '1px solid #E5EAF1', justifyContent: 'flex-end', gap: 1.5 }}>
                    <MaterialButton onClick={onClose} sx={{ color: seasonAccent, fontSize: 16, fontWeight: 700, textTransform: 'none' }}>Avbryt</MaterialButton>
                    <MaterialButton
                        onClick={onSave}
                        disabled={validUserCount === 0 || isGroupMissing}
                        variant="contained"
                        sx={{ px: 4, height: 50, borderRadius: '8px', bgcolor: seasonAccent, fontSize: 16, fontWeight: 700, textTransform: 'none', boxShadow: 'none', '&:hover': { bgcolor: seasonAccentHover, boxShadow: 'none' } }}
                    >
                        Lagre
                    </MaterialButton>
                </DialogActions>
            </Box>
        </Dialog>
    );
}
